import { Link, NavLink } from 'react-router-dom';
import {
  Settings, LogOut, ShieldCheck,
  LayoutDashboard, Clock,
  Search,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCredits } from '../../hooks/useCredits';
import { getSignEmoji } from '../../lib/astrology';
import { useI18n } from '../../hooks/useI18n';
import { useUserRole } from '../../hooks/useUserRole';
import { useNavMenu } from '../../hooks/useNavMenu';
import { ALL_NAV_ITEMS } from '../../constants/navItems';

interface SidebarProps {
  onClose?: () => void;
  onOpenPalette?: () => void;
}

export function Sidebar({ onClose, onOpenPalette }: SidebarProps) {
  const { profile, signOut } = useAuth();
  const { credits, isUnlimited } = useCredits();
  const { t } = useI18n();
  const role = useUserRole();
  const navConfig = useNavMenu();

  async function handleSignOut() {
    onClose?.();
    await signOut();
    window.location.href = '/';
  }

  const firstName = profile?.full_name?.split(' ')[0] ?? '';
  const signEmoji = profile?.sun_sign ? getSignEmoji(profile.sun_sign) : '✦';

  const totalCredits = (isUnlimited ? 1 : credits.questions) +
                       (isUnlimited ? 1 : credits.synastry) +
                       (isUnlimited ? 1 : credits.astromatch);
  const hasCredits = isUnlimited || totalCredits > 0;

  // Merge static definitions with DB config; fall back to defaultOrder if DB not loaded yet
  const orderedItems = [...ALL_NAV_ITEMS].sort((a, b) => {
    const cfgA = navConfig?.find(c => c.id === a.id);
    const cfgB = navConfig?.find(c => c.id === b.id);
    const orderA = cfgA?.sort_order ?? a.defaultOrder;
    const orderB = cfgB?.sort_order ?? b.defaultOrder;
    return orderA - orderB;
  });

  const glyphStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: '1rem',
    color: 'rgba(240, 243, 250, 0.92)',
    flexShrink: 0,
    width: '1.2rem',
    textAlign: 'center',
    lineHeight: 1,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Logo */}
      <div className="sidebar-logo">
        <Link
          to="/"
          onClick={onClose}
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <img src="/logo.svg" alt="" aria-hidden="true" style={{ height: '44px', width: 'auto', display: 'block' }} />
          <span style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: '1.2rem', fontWeight: 400, color: 'var(--bg)', letterSpacing: '0.04em' }}>Professya</span>
        </Link>
        {firstName && (
          <p style={{ fontSize: 'var(--text-13)', color: 'rgba(250, 252, 254, 0.88)', marginTop: '0.3rem', fontFamily: 'var(--font-body)' }}>
            {signEmoji} {firstName}
          </p>
        )}
      </div>

      {/* Search trigger */}
      <div style={{ padding: '0 0.75rem 0.5rem' }}>
        <button
          className="sidebar-search"
          onClick={() => { onClose?.(); onOpenPalette?.(); }}
          aria-label="Buscar ferramentas (Cmd+K)"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(250, 252, 254, 0.07)',
            border: '1px solid var(--line-soft)',
            borderRadius: 'var(--radius-md)',
            padding: '0.6rem 0.875rem',
            color: 'rgba(250, 252, 254, 0.82)',
            cursor: 'pointer',
            fontSize: 'var(--text-13)',
            transition: 'border-color var(--transition-fast)',
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--line-medium)')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--line-soft)')}
        >
          <Search size={15} aria-hidden />
          <span style={{ flex: 1, textAlign: 'left' }}>Buscar...</span>
          <kbd style={{ fontSize: '11px', background: 'rgba(250,252,254,0.10)', padding: '1px 5px', borderRadius: '2px', border: '1px solid rgba(250,252,254,0.20)', lineHeight: 1.5 }}>⌘K</kbd>
        </button>
      </div>

      {/* Navigation, flat list, DB-ordered */}
      <nav className="sidebar-nav" style={{ flex: 1, overflowY: 'auto' }}>
        {orderedItems.map(({ id, to, label, glyph, Icon }) => {
          const cfg = navConfig?.find(c => c.id === id);
          const disabled = navConfig !== null && cfg !== undefined && !cfg.enabled;

          const labelText = label.startsWith('nav.')
            ? t(label as Parameters<typeof t>[0])
            : label;

          if (disabled) {
            return (
              <span
                key={id}
                className="sidebar-item"
                title="Disponível em breve"
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.625rem',
                  opacity: 0.35, cursor: 'default', pointerEvents: 'none',
                }}
                aria-disabled="true"
              >
                {Icon
                  ? <Icon size={16} style={{ flexShrink: 0, color: 'rgba(240, 243, 250, 0.92)' }} aria-hidden />
                  : <span style={glyphStyle} aria-hidden>{glyph}</span>
                }
                <span>{labelText}</span>
                <Clock size={11} style={{ marginLeft: 'auto', opacity: 0.6, flexShrink: 0 }} aria-hidden />
              </span>
            );
          }

          return (
            <NavLink
              key={id}
              to={to}
              onClick={onClose}
              className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
              style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}
            >
              {Icon
                ? <Icon size={16} style={{ flexShrink: 0, color: 'rgba(240, 243, 250, 0.92)' }} aria-hidden />
                : <span style={glyphStyle} aria-hidden>{glyph}</span>
              }
              <span>{labelText}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Credit summary */}
      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--line-soft)' }}>
        {hasCredits ? (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className="credit-pill" title={t('credits.questions')}>
              ⌘ {isUnlimited ? '∞' : credits.questions}
            </span>
            <span className="credit-pill" title={t('credits.synastry')}>
              ∞ {isUnlimited ? '∞' : credits.synastry}
            </span>
            <span className="credit-pill" title={t('credits.astromatch')}>
              ≈ {isUnlimited ? '∞' : credits.astromatch}
            </span>
          </div>
        ) : (
          <Link
            to="/wallet"
            onClick={onClose}
            style={{
              display: 'block',
              textAlign: 'center',
              padding: '0.5rem',
              borderRadius: 'var(--radius-md)',
              border: '1px dashed rgba(250, 252, 254, 0.60)',
              color: 'rgba(240, 243, 250, 0.92)',
              fontSize: 'var(--text-13)',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'background var(--transition-fast)',
            }}
          >
            + Adicionar créditos
          </Link>
        )}
      </div>

      {/* Bottom bar: Portal (astrologers) · Settings · Admin · Sign out */}
      <div style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid var(--line-soft)', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
        {(role === 'admin' || role === 'astrologer') && (
          <NavLink
            to="/profissional/portal"
            onClick={onClose}
            className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
          >
            <LayoutDashboard size={17} style={{ flexShrink: 0 }} aria-hidden />
            <span>Portal do Astrólogo</span>
          </NavLink>
        )}
        <NavLink
          to="/profile"
          onClick={onClose}
          className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
        >
          <Settings size={17} style={{ flexShrink: 0 }} aria-hidden />
          <span>{t('nav.settings')}</span>
        </NavLink>
        {profile?.role === 'admin' && (
          <NavLink
            to="/admin-ap/dashboard"
            onClick={onClose}
            className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
          >
            <ShieldCheck size={17} style={{ flexShrink: 0 }} aria-hidden />
            <span>Painel Admin</span>
          </NavLink>
        )}
        <button className="sidebar-item" onClick={handleSignOut}>
          <LogOut size={17} style={{ flexShrink: 0 }} aria-hidden />
          <span>{t('nav.signOut')}</span>
        </button>
      </div>

    </div>
  );
}
